import React, { useState } from "react";
import { z } from "zod";
import { Controller } from "react-hook-form";
import {
  Box,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import DataTable from '../components/DataTable';
import { FormModal, RHFTextField } from "../components/form";

const COLUMNS = [
  { key: 'roleName', label: 'Role Name' },
  { key: 'description', label: 'Description' },
  {
    key: 'permissions',
    label: 'Permissions',
    render: (row) =>
      Object.entries(row.permissions)
        .filter(([, v]) => v)
        .map(([k]) => k.replace(/([A-Z])/g, " $1").trim())
        .join(", ") || "—",
  },
];

const schema = z.object({
  roleName: z.string().optional(),
  description: z.string().optional(),
  permissions: z.object({
    viewDashboard: z.boolean(),
    manageUsers: z.boolean(),
    editContent: z.boolean(),
    deleteContent: z.boolean(),
    viewReports: z.boolean(),
  }),
});

const defaultValues = {
  roleName: "",
  description: "",
  permissions: {
    viewDashboard: false,
    manageUsers: false,
    editContent: false,
    deleteContent: false,
    viewReports: false,
  },
};

const AddRole = () => {
  const [roles, setRoles] = useState([
    {
      roleName: "Admin",
      description: "Administrator with full access",
      permissions: { viewDashboard: true, manageUsers: true, editContent: true, deleteContent: true, viewReports: true },
    },
    {
      roleName: "Editor",
      description: "Can edit and view content",
      permissions: { viewDashboard: true, manageUsers: false, editContent: true, deleteContent: false, viewReports: true },
    },
  ]);

  const [openModal, setOpenModal] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTargetIndex, setDeleteTargetIndex] = useState(null);

  const handleDeleteClick = (index) => { setDeleteTargetIndex(index); setDeleteConfirmOpen(true); };
  const confirmDelete = () => {
    if (deleteTargetIndex !== null)
      setRoles((prev) => prev.filter((_, i) => i !== deleteTargetIndex));
    setDeleteConfirmOpen(false);
    setDeleteTargetIndex(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <DataTable
        title="Roles"
        columns={COLUMNS}
        rows={roles}
        getRowKey={(row, i) => i}
        toolbarActions={
          <Button variant="contained" color="primary" onClick={() => setOpenModal(true)}>
            Add New Role
          </Button>
        }
        renderActions={(role) => {
          const index = roles.indexOf(role);
          return (
            <>
              <IconButton color="primary" size="small" onClick={() => alert("Edit not implemented")}>
                <EditIcon />
              </IconButton>
              <IconButton color="error" size="small" onClick={() => handleDeleteClick(index)}>
                <DeleteIcon />
              </IconButton>
            </>
          );
        }}
      />

      <FormModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        title="Add New Role"
        schema={schema}
        defaultValues={defaultValues}
        onSubmit={async (data) => {
          const newRole = {
            id: roles.length + 1,
            roleName: data.roleName,
            description: data.description,
            permissions: data.permissions,
          };
          setRoles([...roles, newRole]);
        }}
        submitLabel="Add Role"
        width={400}
      >
        {({ control }) => (
          <>
            <RHFTextField control={control} name="roleName" label="Role Name" />
            <RHFTextField control={control} name="description" label="Description" multiline rows={3} />
            <Typography variant="h6" sx={{ mt: 3 }}>Assign Permissions</Typography>
            <FormGroup>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Controller control={control} name="permissions.viewDashboard" render={({ field }) => (
                    <FormControlLabel control={<Checkbox checked={!!field.value} onChange={field.onChange} />} label="View Dashboard" />
                  )} />
                </Grid>
                <Grid item xs={6}>
                  <Controller control={control} name="permissions.manageUsers" render={({ field }) => (
                    <FormControlLabel control={<Checkbox checked={!!field.value} onChange={field.onChange} />} label="Manage Users" />
                  )} />
                </Grid>
                <Grid item xs={6}>
                  <Controller control={control} name="permissions.editContent" render={({ field }) => (
                    <FormControlLabel control={<Checkbox checked={!!field.value} onChange={field.onChange} />} label="Edit Content" />
                  )} />
                </Grid>
                <Grid item xs={6}>
                  <Controller control={control} name="permissions.deleteContent" render={({ field }) => (
                    <FormControlLabel control={<Checkbox checked={!!field.value} onChange={field.onChange} />} label="Delete Content" />
                  )} />
                </Grid>
                <Grid item xs={6}>
                  <Controller control={control} name="permissions.viewReports" render={({ field }) => (
                    <FormControlLabel control={<Checkbox checked={!!field.value} onChange={field.onChange} />} label="View Reports" />
                  )} />
                </Grid>
              </Grid>
            </FormGroup>
          </>
        )}
      </FormModal>

      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={confirmDelete}
      />
    </Box>
  );
};

export default AddRole;
